/** One documented release, stored at Firestore `releaseNotes/{version}` and written only by `src/_console/release_notes.js`. */
export interface ReleaseNote {
    version: string;
    /** Last documented version before this one; the note covers everything since it. */
    fromVersion: string | null;
    /** major * 1e6 + minor * 1e3 + patch, because version strings do not sort ("3.9" > "3.10"). */
    order: number;
    /** YYYY-MM-DD */
    date: string;
    title: string;
    /** Plain text for the header feed. */
    summary: string;
    /** Markdown for the modal and the history page. */
    bodyMd: string;
}

/** Per-user read/cleared state, stored at Firestore `releaseNoteState/{uid}`. */
export interface ReleaseNoteState {
    /** Every note with `order <= readUpTo` is read. */
    readUpTo: number;
    /** Every note with `order <= clearedUpTo` is hidden from the header feed. */
    clearedUpTo: number;
    readVersions: string[];
    clearedVersions: string[];
}

export const FEED_LIMIT = 10;

export const emptyReleaseNoteState: ReleaseNoteState = {
    readUpTo: 0,
    clearedUpTo: 0,
    readVersions: [],
    clearedVersions: [],
};

const newestOrder = (notes: ReleaseNote[]) => notes.reduce((max, note) => Math.max(max, note.order), 0);

export const BASELINE_FEED_SIZE = 5;

/** A user without saved state gets the newest notes in the feed, with only the newest one unread. */
export const getBaselineState = (notes: ReleaseNote[]): ReleaseNoteState => {
    const newest = newestOrder(notes);
    const oldestShown = notes.map((note) => note.order).sort((a, b) => b - a)[BASELINE_FEED_SIZE - 1];
    return {
        ...emptyReleaseNoteState,
        readUpTo: newest > 0 ? newest - 1 : 0,
        clearedUpTo: oldestShown !== undefined && notes.length > BASELINE_FEED_SIZE ? oldestShown - 1 : 0,
    };
};

export const isReleaseNoteCleared = (note: ReleaseNote, state: ReleaseNoteState) =>
    note.order <= state.clearedUpTo || state.clearedVersions.includes(note.version);

export const isReleaseNoteRead = (note: ReleaseNote, state: ReleaseNoteState) =>
    note.order <= state.readUpTo || state.readVersions.includes(note.version) || isReleaseNoteCleared(note, state);

/** Newest first, cleared notes left out. */
export const getReleaseNoteFeed = (notes: ReleaseNote[], state: ReleaseNoteState, limit = FEED_LIMIT) =>
    notes
        .filter((note) => !isReleaseNoteCleared(note, state))
        .sort((a, b) => b.order - a.order)
        .slice(0, limit);

export const countUnreadReleaseNotes = (notes: ReleaseNote[], state: ReleaseNoteState) =>
    notes.filter((note) => !isReleaseNoteRead(note, state)).length;

/** Moving a watermark to the newest loaded note makes every per-version entry redundant, so the arrays reset. */
export const getMarkAllReadPatch = (
    notes: ReleaseNote[],
    state: ReleaseNoteState,
): Pick<ReleaseNoteState, "readUpTo" | "readVersions"> => ({
    readUpTo: Math.max(state.readUpTo, newestOrder(notes)),
    readVersions: [],
});

export const getClearAllPatch = (notes: ReleaseNote[], state: ReleaseNoteState): ReleaseNoteState => {
    const newest = newestOrder(notes);
    return {
        readUpTo: Math.max(state.readUpTo, newest),
        clearedUpTo: Math.max(state.clearedUpTo, newest),
        readVersions: [],
        clearedVersions: [],
    };
};

/** "2026-10-15" -> "15.10.2026"; the notes are German-only, so the German date format is used for every locale. */
export const formatReleaseNoteDate = (date: string) => date.split("-").reverse().join(".");
